import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  MenuItem,
  Paper,
  Stack,
} from "@mui/material";
import MaterialReactTable from "material-react-table";
import { useAuthUser } from "react-auth-kit";
import PageTitle from "../../components/PageTitle/PageTitle";
import {
  CreateUser,
  CreateOrganization,
  useGetUsers,
  ViewUser,
  ViewOrganization,
  useGetOrganizations,
  useResetPassword,
  usePutOrganization,
  usePutUser,
} from "../../components/HandlingComponents";
import { LoadingPaper, LoadingError } from "../../components/LoadingComponents";
import {
  getUsercolumns,
  getOrgcolumns,
  getUserColumns,
  getOrgColumns,
} from "./UsersColumns";
import KeyIcon from "@mui/icons-material/Key";
import EditIcon from "@mui/icons-material/Edit";
import { useEditMode } from "../../components/EditableComponents";
import UploadFileButton from "../../components/UploadFileButton/UploadFileButton";

function Users() {
  const authUser = useAuthUser();

  // === User list handling ===
  const userListQuery = useGetUsers();
  const orgListQuery = useGetOrganizations();

  // === CreateUser modal handling ===
  const [openCreateUser, setOpenCreateUser] = useState(false);
  const handleOpenCreateUser = () => {
    setOpenCreateUser(true);
  };
  const handleCloseCreateUser = () => {
    setOpenCreateUser(false);
  };

  // === CreateOrganization modal handling ===
  const [openCreateOrganization, setOpenCreateOrganization] = useState(false);
  const handleOpenCreateOrganization = () => {
    setOpenCreateOrganization(true);
  };
  const handleCloseCreateOrganization = () => {
    setOpenCreateOrganization(false);
  };

  // == psw reset ==
  const resetUserPassword = useResetPassword();

  // === EditMode handling ===
  const editOrgMutation = usePutOrganization();
  const editUserMutation = usePutUser();

  // == logo upload ==
  const handleFileChange = (e, id, name) => {
    let img = e.target.files[0];
    editOrgMutation.mutate({
      id: id,
      name: name,
      file: img,
    });
  };

  const {
    editMode: editModeUser,
    enterEditMode: enterEditModeUser,
    cancelEditMode: cancelEditModeUser,
    confirmEditMode: confirmEditModeUser,
    updateEditState: updateEditStateUser,
    setQueryKey: setQueryKeyUser,
  } = useEditMode({
    editModeEnabled: false,
    updateMutation: editUserMutation,
    initialQueryKey: [],
  });
  const {
    editMode: editModeOrg,
    enterEditMode: enterEditModeOrg,
    cancelEditMode: cancelEditModeOrg,
    confirmEditMode: confirmEditModeOrg,
    updateEditState: updateEditStateOrg,
    setQueryKey: setQueryKeyOrg,
  } = useEditMode({
    editModeEnabled: false,
    updateMutation: editOrgMutation,
    initialQueryKey: [],
  });

  const userColumns = getUserColumns(editModeUser, updateEditStateUser);
  const orgColumns = getOrgColumns(editModeOrg, updateEditStateOrg);

  return (
    <main
      style={{
        width: "100%",
      }}
    >
      <Dialog open={openCreateUser}>
        <CreateUser closeModal={handleCloseCreateUser} />
      </Dialog>

      <Dialog open={openCreateOrganization}>
        <CreateOrganization closeModal={handleCloseCreateOrganization} />
      </Dialog>

      <PageTitle title="Cabina di comando" />
      <Stack direction={"row"}>
        <Paper style={{ minHeight: "fitContent", margin: "2vh" }}>
          <Box>
            <Button
              style={{ margin: "15px" }}
              color="primary"
              onClick={handleOpenCreateUser}
              variant="contained"
            >
              CREA UTENTE
            </Button>
          </Box>
          {userListQuery.isLoading ? (
            <LoadingPaper />
          ) : userListQuery.isError ? (
            <LoadingError error={userListQuery.error} />
          ) : (
            <MaterialReactTable
              columns={userColumns}
              data={userListQuery.data?.users || []}
              initialState={{
                density: "compact",
                showColumnFilters: true,
              }}
              enablePagination={true}
              enableBottomToolbar={true}
              enableTopToolbar={false}
              muiTableContainerProps={{
                sx: {
                  overflowX: "hidden",
                },
              }}
              enableSorting={true}
              muiTablePaperProps={{
                elevation: 0,
              }}
              enableColumnResizing
              enableColumnActions={false}
              enableRowSelection={false}
              renderDetailPanel={({ row }) => {
                return (
                  <ViewUser
                    editMode={editModeUser}
                    updateEditState={updateEditStateUser}
                    user_id={row.original.id}
                  />
                );
              }}
              enableRowActions
              positionActionsColumn="last"
              renderRowActionMenuItems={({ closeMenu, row, table }) => [
                <MenuItem
                  key="resetpsw"
                  onClick={() => {
                    resetUserPassword.mutate(row.original.id);
                    closeMenu();
                  }}
                >
                  <IconButton>
                    <KeyIcon color="error" />
                  </IconButton>
                  Reset password
                </MenuItem>,

                <MenuItem
                  key="edit"
                  onClick={() => {
                    setQueryKeyUser(["users", row.original.id]);
                    if (editModeUser) confirmEditModeUser();
                    else enterEditModeUser(row.original.id);
                    closeMenu();
                  }}
                >
                  <IconButton>
                    <EditIcon color={editModeUser ? "success" : "warning"} />
                  </IconButton>
                  {editModeUser === row.original.id
                    ? "Conferma modifiche"
                    : "Modifica"}
                </MenuItem>,
              ]}
            />
          )}
        </Paper>
        {authUser()?.role === 1 && (
          <Paper style={{ minHeight: "fitContent", margin: "2vh" }}>
            <Box>
              <Button
                style={{ margin: "15px" }}
                color="primary"
                onClick={handleOpenCreateOrganization}
                variant="contained"
              >
                CREA ORGANIZZAZIONE
              </Button>
            </Box>
            {orgListQuery.isLoading ? (
              <LoadingPaper />
            ) : orgListQuery.isError ? (
              <LoadingError error={orgListQuery.error} />
            ) : (
              <MaterialReactTable
                columns={orgColumns}
                data={orgListQuery.data?.organizations || []}
                initialState={{
                  density: "compact",
                  showColumnFilters: true,
                }}
                enablePagination={true}
                enableBottomToolbar={true}
                enableTopToolbar={false}
                muiTableContainerProps={{
                  sx: {
                    overflowX: "hidden",
                  },
                }}
                enableSorting={true}
                muiTablePaperProps={{
                  elevation: 0,
                }}
                enableColumnResizing
                enableColumnActions={false}
                enableRowSelection={false}
                renderDetailPanel={({ row }) => {
                  return (
                    <ViewOrganization
                      editMode={editModeOrg}
                      updateEditState={updateEditStateOrg}
                      org_id={row.original.id}
                    />
                  );
                }}
                enableRowActions
                positionActionsColumn="last"
                renderRowActionMenuItems={({ closeMenu, row, table }) => [
                  <MenuItem
                    key="edit"
                    onClick={() => {
                      setQueryKeyOrg(["organizations", row.original.id]);
                      if (editModeOrg) confirmEditModeOrg();
                      else enterEditModeOrg(row.original.id);
                      closeMenu();
                    }}
                  >
                    <IconButton>
                      <EditIcon color={editModeOrg ? "success" : "warning"} />
                    </IconButton>
                    {editModeOrg === row.original.id
                      ? "Conferma modifiche"
                      : "Modifica"}
                  </MenuItem>,
                  <MenuItem key="upload_logo">
                    <UploadFileButton
                      file={null}
                      label="Carica nuovo logo"
                      handleFileChange={(e) => {
                        handleFileChange(e, row.original.id, row.original.name);
                        closeMenu();
                      }}
                    />
                  </MenuItem>,
                ]}
              />
            )}
          </Paper>
        )}
      </Stack>
    </main>
  );
}

export default Users;
